import { Link } from 'react-router';
import { motion } from 'framer-motion';
import { WordMark } from '../../components/WordMark';
import { EDGES } from '../../theme/tokens';

// Shop — placeholder for v2.0. The cosmetic engine + storefront ship in
// v2.1. This page acknowledges that explicitly so first-time visitors
// don't bounce thinking the game is broken; we preview the planned
// categories as disabled tiles so the future is visible.

interface CosmeticCategory {
  glyph: string;
  name: string;
  flavor: string;
  tone: 'cholo' | 'emo' | 'sodium' | 'tecate';
}

const CATEGORIES: CosmeticCategory[] = [
  { glyph: '▤', name: 'Card backs', flavor: 'spray-tag, mural, Pendleton plaid', tone: 'cholo' },
  { glyph: '✶', name: 'Hero portraits', flavor: 'alt looks, faction skins', tone: 'emo' },
  { glyph: '◐', name: 'Board frames', flavor: 'cruise night, backyard show', tone: 'tecate' },
  { glyph: '!', name: 'Emotes', flavor: 'taunts, claims, congrats', tone: 'sodium' },
  { glyph: '∿', name: 'VFX trails', flavor: 'attack arcs, summon rings', tone: 'sodium' },
  { glyph: '♬', name: 'Music packs', flavor: 'banda, punk, neoperreo', tone: 'tecate' },
];

const TONE_COLOR: Record<CosmeticCategory['tone'], string> = {
  cholo: 'text-cholo',
  emo: 'text-emo',
  sodium: 'text-sodium',
  tecate: 'text-tecate',
};

export function ShopPage() {
  return (
    <main className="pt-safe relative h-dvh w-full overflow-hidden bg-asphalt">
      <div
        aria-hidden
        className="pointer-events-none absolute -left-1/4 -top-1/4 h-[60vw] w-[60vw] max-h-[760px] max-w-[760px]"
        style={{
          background:
            'radial-gradient(circle, rgba(212,169,69,0.16) 0%, rgba(212,169,69,0) 70%)',
        }}
      />

      <section className="relative mx-auto flex h-full w-full max-w-6xl flex-col gap-md overflow-y-auto px-md py-sm md:gap-lg md:px-lg md:py-md lg:gap-xl lg:py-xl">
        <header className="flex items-center justify-between">
          <span className="md:hidden">
            <WordMark size="sm" />
          </span>
          <span className="hidden md:inline lg:hidden">
            <WordMark size="md" />
          </span>
          <span className="hidden lg:inline">
            <WordMark size="lg" />
          </span>
          <Link
            to="/menu"
            className="font-data text-[12px] uppercase tracking-[0.3em] text-graphite hover:text-marker md:text-[12px] lg:text-[14px]"
          >
            ← menu
          </Link>
        </header>

        <div>
          <p className="font-data text-[12px] uppercase tracking-[0.3em] text-marker md:text-[12px]">
            /shop · coming v2.1
          </p>
          <h1 className="mt-xs font-cholo text-[28px] leading-none text-tecate md:text-[48px] lg:text-[72px]">
            Cosmetics
          </h1>
          <p className="mt-xs hidden max-w-md text-[14px] text-marker md:block md:max-w-2xl md:text-[16px] lg:max-w-3xl lg:text-[18px]">
            All cosmetics are procedurally generated — no art assets to download.
            Everything earnable, none for sale. The storefront opens after the
            content engine ships in v2.1.
          </p>
        </div>

        {/* Category preview grid — 3-up on landscape phones, 3-up on tablet, 3-up on desktop.
            All disabled, but visible so the player can see where the experience is heading. */}
        <div className="grid grid-cols-3 gap-sm md:gap-lg">
          {CATEGORIES.map((cat, i) => (
            <CategoryTile key={cat.name} category={cat} index={i} />
          ))}
        </div>

        <p className="text-center font-data text-[12px] uppercase tracking-[0.3em] text-graphite md:text-[12px]">
          want to follow the build? · serifeus studio
        </p>
      </section>
    </main>
  );
}

function CategoryTile({ category, index }: { category: CosmeticCategory; index: number }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.04 * index, duration: 0.3, ease: 'easeOut' }}
      className="group relative flex min-h-[96px] flex-col gap-xs overflow-hidden rounded-md border bg-stucco1 p-sm opacity-70 md:min-h-[140px] md:gap-sm md:p-lg"
      style={{ borderColor: EDGES.faint }}
    >
      {/* Locked banner — diagonal ribbon top-right */}
      <span
        className="pointer-events-none absolute -right-12 top-3 inline-block rotate-[35deg] bg-stucco3 px-12 py-[2px] font-data text-[13px] uppercase tracking-[0.3em] text-graphite md:text-[10px]"
        aria-hidden
      >
        locked
      </span>

      <div className="flex items-baseline gap-sm">
        <span
          className={`font-data text-[24px] leading-none ${TONE_COLOR[category.tone]}`}
          aria-hidden
        >
          {category.glyph}
        </span>
        <h3
          className={`font-cholo text-[22px] leading-none md:text-[26px] lg:text-[30px] ${TONE_COLOR[category.tone]}`}
        >
          {category.name}
        </h3>
      </div>
      <p className="text-[13px] text-marker md:text-[14px]">{category.flavor}</p>
      <p className="mt-auto font-data text-[12px] uppercase tracking-[0.2em] text-graphite md:text-[11px]">
        unlocks in v2.1
      </p>
    </motion.div>
  );
}
